import _ from 'lodash'
import React, {
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  FlatListProps,
  ImageStyle,
  StyleProp,
  TextProps,
  TextStyle,
} from 'react-native'
import {
  Dimensions,
  FlatList,
  I18nManager,
  Keyboard,
  KeyboardEvent,
  Modal,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableHighlight,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native'
import { reorderList } from '../utils/languageSelector'

export type IDropdownRef = {
  open: () => void
  close: () => void
}

export interface DropdownProps<T> {
  visible: boolean
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  sourceLanguage: string
  ref?:
    | React.RefObject<IDropdownRef>
    | React.MutableRefObject<IDropdownRef>
    | null
    | undefined
  testID?: string
  itemTestIDField?: string
  style?: StyleProp<ViewStyle>
  containerStyle?: StyleProp<ViewStyle>
  placeholderStyle?: StyleProp<TextStyle>
  selectedTextStyle?: StyleProp<TextStyle>
  selectedTextProps?: TextProps
  itemContainerStyle?: StyleProp<ViewStyle>
  itemTextStyle?: StyleProp<TextStyle>
  inputSearchStyle?: StyleProp<TextStyle>
  iconStyle?: StyleProp<ImageStyle>
  maxHeight?: number
  minHeight?: number
  fontFamily?: string
  iconColor?: string
  activeColor?: string
  data: T[]
  value?: T | string | null | undefined
  placeholder?: string
  labelField: keyof T
  valueField: keyof T
  searchField?: keyof T
  search?: boolean
  searchPlaceholder?: string
  disable?: boolean
  autoScroll?: boolean
  showsVerticalScrollIndicator?: boolean
  dropdownPosition?: 'auto' | 'top' | 'bottom'
  flatListProps?: Omit<FlatListProps<any>, 'renderItem' | 'data'>
  keyboardAvoiding?: boolean
  backgroundColor?: string
  confirmSelectItem?: boolean
  accessibilityLabel?: string
  itemAccessibilityLabelField?: string
  inverted?: boolean
  mode?: 'default' | 'modal'
  onChange: (item: T) => void
  renderLeftIcon?: (visible?: boolean) => JSX.Element | null | undefined
  renderRightIcon?: (visible?: boolean) => JSX.Element | null | undefined
  renderItem?: (item: T, selected?: boolean) => JSX.Element | null | undefined
  renderInputSearch?: (
    onSearch: (text: string) => void
  ) => JSX.Element | null | undefined
  onFocus?: () => void
  onBlur?: () => void
  searchQuery?: (keyword: string, labelValue: string) => boolean
  onChangeText?: (search: string) => void
  onConfirmSelectItem?: (item: T) => void
  scrollable?: boolean
}

const statusBarHeight: number = StatusBar.currentHeight || 0

const LanguageDropdownComponent: <T>(
  props: DropdownProps<T>
) => ReactElement<any, string | JSXElementConstructor<any>> | null =
  React.forwardRef((props, currentRef) => {
    const {
      visible,
      setVisible,
      sourceLanguage,
      testID,
      itemTestIDField,
      onChange,
      style = {},
      containerStyle,
      placeholderStyle,
      selectedTextStyle,
      itemContainerStyle,
      itemTextStyle,
      selectedTextProps = {},
      data = [],
      labelField,
      valueField,
      value,
      activeColor = '#ebebeb',
      fontFamily,
      placeholder = 'Select item',
      maxHeight = 340,
      minHeight = 0,
      disable = false,
      keyboardAvoiding = true,
      inverted = true,
      renderLeftIcon,
      renderRightIcon = () => null,
      renderItem,
      onFocus,
      onBlur,
      autoScroll = true,
      showsVerticalScrollIndicator = true,
      dropdownPosition = 'auto',
      flatListProps,
      backgroundColor,
      onChangeText,
      confirmSelectItem,
      onConfirmSelectItem,
      accessibilityLabel,
      itemAccessibilityLabelField,
      mode = 'default',
      scrollable,
    } = props

    const ref = useRef<View>(null)
    const refList = useRef<FlatList>(null)
    const [currentValue, setCurrentValue] = useState<any>(null)
    const [listData, setListData] = useState<any[]>(data)
    const [position, setPosition] = useState<any>()
    const [keyboardHeight, setKeyboardHeight] = useState<number>(0)
    const { width: W, height: H } = Dimensions.get('window')

    const styleContainerVertical: ViewStyle = useMemo(() => {
      return {
        backgroundColor: 'rgba(0,0,0,0.1)',
        alignItems: 'center',
      }
    }, [])
    const styleHorizontal: ViewStyle = useMemo(() => {
      return {
        width: '100%',
        alignSelf: 'center',
      }
    }, [W])
    useImperativeHandle(currentRef, () => {
      return { open: eventOpen, close: eventClose }
    })
    useEffect(() => {
      const reorderedList = reorderList(data, sourceLanguage)
      setListData(reorderedList)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, sourceLanguage])

    const eventOpen = () => {
      if (!disable) {
        setVisible(true)
        if (onFocus) {
          onFocus()
        }

        scrollIndex()
      }
    }

    const eventClose = useCallback(() => {
      if (!disable) {
        setVisible(false)
        if (onBlur) {
          onBlur()
        }
      }
    }, [disable, onBlur])

    const font = useCallback(() => {
      if (fontFamily) {
        return {
          fontFamily: fontFamily,
        }
      } else {
        return {}
      }
    }, [fontFamily])

    const _measure = useCallback(() => {
      if (ref && ref?.current) {
        ref.current.measureInWindow((pageX, pageY, width, height) => {
          const isFull = mode === 'modal'
          const top = isFull ? 20 : height + pageY + 2
          const bottom = H - top + height
          const left = I18nManager.isRTL ? W - width - pageX : pageX

          setPosition({
            isFull,
            width: Math.floor(width),
            top: Math.floor(top + statusBarHeight),
            bottom: Math.floor(bottom - statusBarHeight),
            left: Math.floor(left),
            height: Math.floor(height),
          })
        })
      }
    }, [H, W, mode])

    const onKeyboardDidShow = useCallback(
      (e: KeyboardEvent) => {
        _measure()
        setKeyboardHeight(e.endCoordinates.height)
      },
      [_measure]
    )

    const onKeyboardDidHide = useCallback(() => {
      setKeyboardHeight(0)
      _measure()
    }, [_measure])

    useEffect(() => {
      const susbcriptionKeyboardDidShow = Keyboard.addListener(
        'keyboardDidShow',
        onKeyboardDidShow
      )
      const susbcriptionKeyboardDidHide = Keyboard.addListener(
        'keyboardDidHide',
        onKeyboardDidHide
      )

      return () => {
        if (typeof susbcriptionKeyboardDidShow?.remove === 'function') {
          susbcriptionKeyboardDidShow.remove()
        }

        if (typeof susbcriptionKeyboardDidHide?.remove === 'function') {
          susbcriptionKeyboardDidHide.remove()
        }
      }
    }, [onKeyboardDidHide, onKeyboardDidShow])

    const getValue = useCallback(() => {
      const defaultValue =
        typeof value === 'object' ? _.get(value, valueField) : value

      const getItem = data.filter((e) =>
        _.isEqual(defaultValue, _.get(e, valueField))
      )
      if (getItem.length > 0) {
        setCurrentValue(getItem[0])
      } else {
        setCurrentValue(null)
      }
    }, [data, value, valueField])

    useEffect(() => {
      getValue()
    }, [value, data, getValue])

    const scrollIndex = useCallback(() => {
      if (autoScroll && data.length > 0 && listData.length === data.length) {
        setTimeout(() => {
          if (refList && refList?.current) {
            const defaultValue =
              typeof value === 'object' ? _.get(value, valueField) : value

            const index = _.findIndex(listData, (e: any) =>
              _.isEqual(defaultValue, _.get(e, valueField))
            )
            if (index > -1 && index <= listData.length - 1) {
              refList?.current?.scrollToIndex({
                index: index,
                animated: false,
              })
            }
          }
        }, 200)
      }
    }, [autoScroll, data.length, listData, value, valueField])

    const showOrClose = useCallback(() => {
      if (!disable) {
        if (keyboardHeight > 0 && visible) {
          return Keyboard.dismiss()
        }

        _measure()
        setVisible(!visible)
        setListData(data)

        if (!visible) {
          if (onFocus) {
            onFocus()
          }
        } else {
          if (onBlur) {
            onBlur()
          }
        }
        scrollIndex()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      disable,
      keyboardHeight,
      visible,
      _measure,
      data,
      scrollIndex,
      onFocus,
      onBlur,
    ])

    const onSelect = useCallback(
      (item: any) => {
        if (confirmSelectItem && onConfirmSelectItem) {
          return onConfirmSelectItem(item)
        }

        if (onChangeText) {
          onChangeText('')
        }
        setCurrentValue(item)
        onChange(item)
        eventClose()
      },
      [
        confirmSelectItem,
        eventClose,
        onChange,
        onChangeText,
        onConfirmSelectItem,
      ]
    )

    const _renderDropdown = () => {
      const isSelected = currentValue && _.get(currentValue, valueField)
      return (
        <TouchableWithoutFeedback
          testID={testID}
          accessible={!!accessibilityLabel}
          accessibilityLabel={accessibilityLabel}
          onPress={showOrClose}
        >
          <View style={styles.dropdown}>
            {renderLeftIcon?.(visible)}
            <Text
              style={[
                styles.textItem,
                isSelected !== null ? selectedTextStyle : placeholderStyle,
                font(),
              ]}
              {...selectedTextProps}
            >
              {isSelected !== null
                ? _.get(currentValue, labelField)
                : placeholder}
            </Text>
            {renderRightIcon(visible)}
          </View>
        </TouchableWithoutFeedback>
      )
    }

    const _renderItem = useCallback(
      ({ item, index }: { item: any; index: number }) => {
        const isSelected = currentValue && _.get(currentValue, valueField)
        const selected = _.isEqual(_.get(item, valueField), isSelected)
        _.assign(item, { _index: index })

        const isGoogleTranslateElement =
          renderItem &&
          renderItem(
            item,
            isSelected
          )?.props.children[0].props.source.uri.includes(
            'google_translate_logo'
          )

        return (
          <TouchableHighlight
            key={index.toString()}
            testID={_.get(item, itemTestIDField || labelField)}
            accessible={!!accessibilityLabel}
            accessibilityLabel={_.get(
              item,
              itemAccessibilityLabelField || labelField
            )}
            underlayColor={activeColor}
            //@ts-ignore
            disabled={sourceLanguage && index == 0}
            onPress={() => onSelect(item)}
          >
            <View
              style={StyleSheet.flatten([
                itemContainerStyle,
                isGoogleTranslateElement && {
                  borderBottomColor: '#ececec',
                  borderBottomWidth: '1px',
                },
                selected && {
                  backgroundColor: activeColor,
                },
              ])}
            >
              {renderItem ? (
                renderItem(item, selected)
              ) : (
                <View style={styles.item}>
                  <Text
                    style={StyleSheet.flatten([
                      styles.textItem,
                      itemTextStyle,
                      font(),
                    ])}
                  >
                    {_.get(item, labelField)}
                  </Text>
                </View>
              )}
            </View>
          </TouchableHighlight>
        )
      },
      [
        accessibilityLabel,
        activeColor,
        currentValue,
        font,
        itemAccessibilityLabelField,
        itemContainerStyle,
        itemTestIDField,
        itemTextStyle,
        labelField,
        onSelect,
        renderItem,
        valueField,
      ]
    )

    const _renderList = useCallback(
      (isTopPosition: boolean) => {
        const isInverted = !inverted ? false : isTopPosition

        const _renderListHelper = () => {
          return (
            <FlatList
              testID={testID + ' flatlist'}
              accessibilityLabel={accessibilityLabel + ' flatlist'}
              {...flatListProps}
              keyboardShouldPersistTaps="handled"
              ref={refList}
              onScrollToIndexFailed={scrollIndex}
              data={listData}
              inverted={isTopPosition ? inverted : false}
              renderItem={_renderItem}
              keyExtractor={(_item, index) => index.toString()}
              showsVerticalScrollIndicator={showsVerticalScrollIndicator}
            />
          )
        }

        return (
          <TouchableWithoutFeedback>
            <View style={styles.flexShrink}>
              {isInverted && _renderListHelper()}
              {!isInverted && _renderListHelper()}
            </View>
          </TouchableWithoutFeedback>
        )
      },
      [
        _renderItem,
        accessibilityLabel,
        flatListProps,
        listData,
        inverted,
        scrollIndex,
        showsVerticalScrollIndicator,
        testID,
      ]
    )

    const _renderModal = useCallback(() => {
      if (visible && position) {
        const { isFull, width, height, top, bottom, left } = position

        const onAutoPosition = () => {
          if (keyboardHeight > 0) {
            return bottom < keyboardHeight + height
          }

          return bottom < 100
        }

        if (width && top && bottom) {
          const styleVertical: ViewStyle = {
            left: left,
            maxHeight: maxHeight,
            minHeight: minHeight,
          }
          const isTopPosition =
            dropdownPosition === 'auto'
              ? onAutoPosition()
              : dropdownPosition === 'top'

          let keyboardStyle: ViewStyle = {}

          let extendHeight = !isTopPosition ? top : bottom
          if (
            keyboardAvoiding &&
            keyboardHeight > 0 &&
            isTopPosition &&
            dropdownPosition === 'auto'
          ) {
            extendHeight = keyboardHeight
          }

          return (
            <Modal
              transparent
              statusBarTranslucent
              visible={visible}
              supportedOrientations={['landscape', 'portrait']}
              onRequestClose={showOrClose}
            >
              <TouchableWithoutFeedback onPress={showOrClose}>
                <View
                  style={StyleSheet.flatten([
                    styles.flex1,
                    isFull && styleContainerVertical,
                    backgroundColor && { backgroundColor: backgroundColor },
                    keyboardStyle,
                  ])}
                >
                  <View
                    style={StyleSheet.flatten([
                      styles.flex1,
                      {
                        width,
                      },
                      !isTopPosition
                        ? { paddingTop: extendHeight }
                        : {
                            justifyContent: 'flex-end',
                            paddingBottom: extendHeight,
                          },
                      isFull && styles.fullScreen,
                    ])}
                  >
                    <ListWrapper
                      scrollable={!!scrollable}
                      style={StyleSheet.flatten([
                        styles.container,
                        isFull ? styleHorizontal : styleVertical,
                        containerStyle,
                      ])}
                    >
                      {_renderList(isTopPosition)}
                    </ListWrapper>
                  </View>
                </View>
              </TouchableWithoutFeedback>
            </Modal>
          )
        }
        return null
      }
      return null
    }, [
      visible,
      position,
      keyboardHeight,
      maxHeight,
      minHeight,
      dropdownPosition,
      keyboardAvoiding,
      showOrClose,
      styleContainerVertical,
      backgroundColor,
      containerStyle,
      styleHorizontal,
      _renderList,
    ])

    return (
      <View
        style={StyleSheet.flatten([styles.mainWrap, style])}
        ref={ref}
        onLayout={_measure}
      >
        {_renderDropdown()}
        {_renderModal()}
      </View>
    )
  })

function ListWrapper({
  scrollable,
  ...props
}: {
  scrollable: boolean
  style: StyleProp<ViewStyle>
  children: ReactNode
}) {
  if (scrollable) return <ScrollView {...props} />
  return <View {...props} />
}

export default LanguageDropdownComponent

export const styles = StyleSheet.create({
  mainWrap: {
    justifyContent: 'center',
  },
  container: {
    flexShrink: 1,
    borderWidth: 0.5,
    borderColor: '#EEEEEE',
    backgroundColor: 'white',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.2,
    shadowRadius: 1.41,
    elevation: 2,
  },
  flex1: {
    flex: 1,
  },
  flexShrink: {
    flexShrink: 1,
  },
  wrapTop: {
    justifyContent: 'flex-end',
  },
  dropdown: {
    flexDirection: I18nManager.isRTL ? 'row-reverse' : 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 35,
  },
  title: {
    marginVertical: 5,
    fontSize: 16,
    writingDirection: I18nManager.isRTL ? 'rtl' : 'ltr',
  },
  item: {
    padding: 17,
    flexDirection: I18nManager.isRTL ? 'row-reverse' : 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  textItem: {
    flex: 1,
    fontSize: 16,
    writingDirection: I18nManager.isRTL ? 'rtl' : 'ltr',
  },
  icon: {
    width: 20,
    height: 20,
  },
  input: {
    borderWidth: 0.5,
    borderColor: '#DDDDDD',
    paddingHorizontal: 8,
    marginBottom: 8,
    margin: 6,
    height: 45,
  },
  fullScreen: {
    alignItems: 'center',
    justifyContent: 'center',
  },
})
